import restClient from 'config/httpClient';
import { call, put, takeEvery } from 'redux-saga/effects';

import { toPureBase64 } from 'utils/string';
import { getSignedUploadUrl, uploadMedia as postToSignedUrl } from 'services/api/MediaService';
import { checkUploadRules, getUploadError, loadPicture, transformFileToBase64 } from 'utils/upload';
import { createNotification } from 'modules/notifications/actions';

import * as actions from './actions';

export function* uploadInTwoSteps({ payload }) {
  const { url, file, mediaId, additionalData, onRequest, onProgress, onSuccess, onError, call: retry } = payload;

  try {
    yield call(onRequest, { file, mediaId, isLoading: true, ...additionalData });

    const { id, url: signedUrl, fields, uploadId } = yield call(getSignedUploadUrl, url, {
      contentType: additionalData?.contentType,
      filename: additionalData?.name,
    });

    if (!id || !signedUrl || !fields) {
      throw new Error('Missing id, url or fields');
    }

    yield call(postToSignedUrl, file, signedUrl, fields, onProgress);
    const path = yield call(transformFileToBase64, file);

    yield call(onSuccess, { ...additionalData, path, id, uploadId, mediaId, isLoading: false });
    yield put(actions.uploadInTwoStepsSuccess(mediaId));
  } catch (error) {
    yield put(actions.uploadInTwoStepsFailure(mediaId, error));
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'web.custom-template.error.unknown',
          defaultMessage: 'An error has occurred while the upload of file.',
        },
      })
    );
    yield call(onError, { file, error, mediaId, isLoading: false, call: retry, ...additionalData });
  }
}

export function* uploadInOneStep({ payload }) {
  const { key, request, image, limits, onSuccess } = payload;

  try {
    yield call(checkUploadRules, image, limits);

    const fileData = yield call(loadPicture, image);
    const b64 = yield call(toPureBase64, fileData);
    const uploadRequest = { ...request, data: b64, url: `${request.url}&extension=${image.name?.split('.').pop()}` };

    const { data } = yield call(restClient.request, uploadRequest);
    const result = { ...data, fileData: URL.createObjectURL(image) };

    yield call(onSuccess, result);
    yield put(actions.uploadInOneStepSuccess(key, result));
    yield put(
      createNotification({
        type: 'success',
        message: { id: 'web.upload.success', defaultMessage: 'The file has been uploaded with success' },
      })
    );
  } catch (error) {
    yield put(actions.uploadInOneStepFailure(key, error));
    yield put(
      createNotification({
        type: 'error',
        message: { id: getUploadError(error.message), defaultMessage: 'The file was not uploaded.' },
      })
    );
  }
}

export default function* mediaSagas() {
  yield takeEvery(actions.uploadInTwoSteps, uploadInTwoSteps);
  yield takeEvery(actions.uploadInOneStep, uploadInOneStep);
}
